import { useState } from 'react';
import { useGameStore } from '../store';
import { CARD_POOL } from '../data/cardTemplates';
import { RARITY } from '../data/cards';

/**
 * Comprehensive admin panel for testing all game mechanics.
 * Collapsible floating panel in bottom-right corner.
 */
export function AdminPanel() {
  const [isExpanded, setIsExpanded] = useState(false);

  // Store state
  const gameDate = useGameStore((state) => state.gameDate);
  const accounts = useGameStore((state) => state.accounts);
  const deposits = useGameStore((state) => state.deposits);

  // Store actions
  const setGameDate = useGameStore((state) => state.setGameDate);
  const advanceGameDate = useGameStore((state) => state.advanceGameDate);
  const addM = useGameStore((state) => state.addM);
  const setAccountBalance = useGameStore((state) => state.setAccountBalance);
  const resetAllData = useGameStore((state) => state.resetAllData);
  const recordDeposit = useGameStore((state) => state.recordDeposit);
  const recordWithdrawal = useGameStore((state) => state.recordWithdrawal);
  const recordRoundUp = useGameStore((state) => state.recordRoundUp);
  const recordImpulseSave = useGameStore((state) => state.recordImpulseSave);
  const claimDailyBonus = useGameStore((state) => state.claimDailyBonus);
  const claimDailyDraw = useGameStore((state) => state.claimDailyDraw);
  const drawCard = useGameStore((state) => state.drawCard);
  const grantSpecificCard = useGameStore((state) => state.grantSpecificCard);

  // Local state for card selection
  const [selectedCardId, setSelectedCardId] = useState('');

  // Build flat list of all cards for dropdown
  const allCards = [
    ...CARD_POOL[RARITY.COMMON],
    ...CARD_POOL[RARITY.RARE],
    ...CARD_POOL[RARITY.EPIC],
    ...CARD_POOL[RARITY.LEGENDARY]
  ];

  const handleDateChange = (e) => {
    setGameDate(e.target.value);
  };

  const handleAccountChange = (accountKey, value) => {
    const numValue = parseFloat(value) || 0;
    setAccountBalance(accountKey, numValue);
  };

  const handleReset = () => {
    if (window.confirm('Reset all game data? This cannot be undone.')) {
      resetAllData();
    }
  };

  const handleWithdrawal = () => {
    if (deposits.length > 0) {
      recordWithdrawal(deposits[0].id, 10);
    }
  };

  const handleGrantCard = () => {
    if (selectedCardId && grantSpecificCard) {
      grantSpecificCard(selectedCardId);
    }
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="fixed bottom-4 right-2 sm:right-4 px-3 py-2 text-xs font-pixel bg-gray-900 text-teal rounded border border-gray-700 hover:border-teal z-50"
      >
        Admin
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 inset-x-2 sm:inset-x-auto sm:right-4 w-auto sm:w-80 max-h-[80vh] overflow-y-auto p-3 sm:p-4 bg-gray-900 rounded-lg border border-gray-700 z-50">
      {/* Header with collapse */}
      <div className="flex justify-between items-center mb-3">
        <span className="text-xs font-pixel text-teal">Admin Panel</span>
        <button
          onClick={() => setIsExpanded(false)}
          className="text-gray-500 hover:text-gray-300 text-lg leading-none"
        >
          ×
        </button>
      </div>

      {/* 1. Simulated Accounts */}
      <Section title="Simulated Accounts">
        <div className="space-y-2">
          {[
            { key: 'HYSA', label: 'HYSA' },
            { key: 'fourOhOneK', label: '401k' },
            { key: 'brokerage', label: 'Brokerage' }
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2">
              <label className="text-xs text-gray-400 w-20 flex-shrink-0">{label}</label>
              <input
                type="number"
                value={accounts[key]}
                onChange={(e) => handleAccountChange(key, e.target.value)}
                className="flex-1 min-w-0 px-2 py-2 sm:py-1 text-xs font-pixel bg-gray-800 text-gray-300 rounded border border-gray-600"
              />
            </div>
          ))}
        </div>
      </Section>

      {/* 2. Action Triggers */}
      <Section title="Action Triggers">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <ActionButton onClick={() => recordDeposit(10)} label="Deposit $10" />
          <ActionButton onClick={() => recordDeposit(100)} label="Deposit $100" />
          <ActionButton
            onClick={handleWithdrawal}
            label="Withdraw $10"
            disabled={deposits.length === 0}
          />
          <ActionButton onClick={recordRoundUp} label="Round-Up" />
          <ActionButton onClick={recordImpulseSave} label="Impulse Save" />
          <ActionButton onClick={claimDailyBonus} label="Daily Login" />
          <ActionButton onClick={() => addM(100)} label="+100 M" accent />
          <ActionButton onClick={() => addM(1000)} label="+1000 M" accent />
        </div>
      </Section>

      {/* 3. Date Controls */}
      <Section title="Date Controls">
        <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mb-2">
          <ActionButton onClick={() => advanceGameDate(1)} label="+1 Day" small />
          <ActionButton onClick={() => advanceGameDate(7)} label="+7 Days" small />
          <ActionButton onClick={() => advanceGameDate(30)} label="+30 Days" small />
        </div>
        <input
          type="date"
          value={gameDate}
          onChange={handleDateChange}
          className="w-full px-2 py-2 sm:py-1 text-xs font-pixel bg-gray-800 text-gray-300 rounded border border-gray-600"
        />
      </Section>

      {/* 4. Card Controls */}
      <Section title="Card Controls">
        <div className="flex flex-col gap-2 mb-2 sm:flex-row">
          <ActionButton onClick={claimDailyDraw} label="Daily Draw" />
          <ActionButton onClick={() => drawCard()} label="Random Card" />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            value={selectedCardId}
            onChange={(e) => setSelectedCardId(e.target.value)}
            className="w-full sm:flex-1 px-2 py-2 sm:py-1 text-xs font-pixel bg-gray-800 text-gray-300 rounded border border-gray-600"
          >
            <option value="">Select card...</option>
            {allCards.map((card) => (
              <option key={card.cardId} value={card.cardId}>
                {card.name} ({card.rarity})
              </option>
            ))}
          </select>
          <ActionButton
            onClick={handleGrantCard}
            label="Grant"
            disabled={!selectedCardId || !grantSpecificCard}
          />
        </div>
      </Section>

      {/* 5. Reset */}
      <Section title="Reset">
        <button
          onClick={handleReset}
          className="w-full px-3 py-2 text-xs font-pixel bg-red-900/30 text-red-400 rounded border border-red-800 hover:bg-red-900/50"
        >
          Reset All Data
        </button>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4">
      <h3 className="text-xs font-pixel text-gray-500 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function ActionButton({ onClick, label, disabled, accent, small }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${small ? 'px-2 py-2 sm:py-1' : 'px-3 py-2 sm:py-1'}
        w-full sm:w-auto
        text-xs font-pixel rounded border
        transition-colors duration-150
        ${disabled
          ? 'bg-gray-800 text-gray-600 border-gray-700 cursor-not-allowed'
          : accent
            ? 'bg-teal/20 text-teal border-teal/50 hover:bg-teal/30'
            : 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700 hover:border-gray-500'
        }
      `}
    >
      {label}
    </button>
  );
}
